import {
  ORDER_CANCELLED,
  ORDER_CONFIRMED,
  ORDER_PLACED,
  ORDER_RECEIVED,
} from "../../constants/orderStatus";
import { PERMISSIONS } from "../../constants/permissions";

// 注文ステータスの遷移ルール(純関数)。firebaseに依存しないためJestで直接テストできる。
//
// かつて遷移可否の判定は3ファイルに重複定義されており(一括=ConfirmStatusChangeModal、
// 単票=OrderedProducts、加えてEditOrderModalに呼び出しゼロの死んだ定義)、
// 一括と単票で許可する遷移が食い違っていた。ここに一本化する。
//
// ルール:
//   前進(注文済→発注済→検収済)は1段階ずつのみ。飛び越しは禁止
//     (発注書メールを送らずに検収済になるのを防ぐ)
//   後退(検収済→発注済、検収済→注文済、発注済→注文済)は任意の前工程へ戻せる
//     ※かつて後退の手段が無く「取消を経由して戻す」しかなかったため、
//       経由中に placed_at / canceled_at が現在時刻で汚染されていた(2026-08の不具合)
//   取消はどの状態からでも可能
//   取消済からの復帰は 注文済 / 発注済 のみ(検収済への復帰は禁止)

// 工程順。取消済は工程外なのでこの並びには含めない
export const STATUS_SEQUENCE = [ORDER_PLACED, ORDER_CONFIRMED, ORDER_RECEIVED];

export const TRANSITION_FORWARD = "forward"; // 前進(1段階)
export const TRANSITION_BACKWARD = "backward"; // 後退(工程を戻す)
export const TRANSITION_RESTORE = "restore"; // 取消済からの復帰
export const TRANSITION_CANCEL = "cancel"; // 取消

// 日付のメンテナンスやステータスの巻き戻しを許可する権限
export const ORDER_ADMIN_ROLES = [
  PERMISSIONS.ADMINISTRATOR,
  PERMISSIONS.HUMAN_RESOURCE_USER,
  PERMISSIONS.ORDER_USER,
];

export const isOrderAdminRole = (role) => ORDER_ADMIN_ROLES.includes(role);

// 遷移の種別を返す。遷移できない場合はnull
export const getTransitionKind = (from, to) => {
  if (!from || !to || from === to) {
    return null;
  }
  if (to === ORDER_CANCELLED) {
    return TRANSITION_CANCEL;
  }
  if (from === ORDER_CANCELLED) {
    // 復帰先は工程の途中(検収済)にはできない
    return to === ORDER_RECEIVED ? null : TRANSITION_RESTORE;
  }
  const fromIndex = STATUS_SEQUENCE.indexOf(from);
  const toIndex = STATUS_SEQUENCE.indexOf(to);
  if (fromIndex < 0 || toIndex < 0) {
    return null;
  }
  if (toIndex < fromIndex) {
    return TRANSITION_BACKWARD;
  }
  return toIndex === fromIndex + 1 ? TRANSITION_FORWARD : null;
};

export const canTransition = (from, to) => getTransitionKind(from, to) !== null;

// 工程を戻す操作か(確認ダイアログで警告を出す対象)
export const isRollback = (from, to) =>
  getTransitionKind(from, to) === TRANSITION_BACKWARD;

// ログインユーザがこの注文をこのステータスへ変更できるか。
// 一般ユーザは「自分が注文した注文済を取消する」場合のみ許可(従来どおり)。
export const canUserTransition = (user, order, targetStatus) => {
  if (!canTransition(order?.status, targetStatus)) {
    return false;
  }
  if (isOrderAdminRole(user?.role)) {
    return true;
  }
  if (user?.role !== PERMISSIONS.NORMAL_USER) {
    return false;
  }
  return (
    targetStatus === ORDER_CANCELLED &&
    order?.status === ORDER_PLACED &&
    !!user?.email &&
    order?.login_user_email === user.email
  );
};

// 日付のメンテナンス(ステータス変更時の日付指定・日付のみの編集)を許可するか。
// 一般ユーザには一切許可しない。
export const canUserEditDates = (user) => isOrderAdminRole(user?.role);

// 一括更新の可否。従来どおり「選択した全件が実行可能」なときだけ実行する。
export const checkBulkTransition = (targetStatus, orders, user) => {
  const list = orders || [];
  const unUpdatables = list.filter(
    (order) => !canUserTransition(user, order, targetStatus)
  );
  return {
    isUpdatable: list.length > 0 && unUpdatables.length === 0,
    unUpdatables,
  };
};

// 発注書メールを送る対象の注文だけを抜き出す。
// 注文済/取消済からの発注は仕入先へ注文書が必要だが、検収済から発注済へ戻すだけの
// 操作では既に注文書を送付済みのため対象外にする(二重送信の防止)。
//
// 前進と戻しを1回の一括操作に混ぜられるようになったため、
// 「フォームを開くか」だけでなく「どの注文を注文書に載せるか」もここで決める。
// 選択全件をそのまま注文書に載せると、戻した注文の注文書が仕入先へ再送されてしまう。
export const getMailFormTargets = (targetStatus, orders) => {
  if (targetStatus !== ORDER_CONFIRMED) {
    return [];
  }
  return (orders || []).filter(
    (order) =>
      order?.status === ORDER_PLACED || order?.status === ORDER_CANCELLED
  );
};

// 発注書メールフォームを開くべきか。
// 対象が1件でもあれば開く(判定が getMailFormTargets とずれないよう必ずこちらを経由する)。
export const shouldOpenMailForm = (targetStatus, orders) =>
  getMailFormTargets(targetStatus, orders).length > 0;

// モバイル端末で実行を止めるべきか。
// 止める目的は「メールフォームを出せないのにステータスだけ発注済になる」の防止なので、
// そもそもメールフォームが不要な操作(検収済からの戻しのみ)は許可する。
export const isBlockedOnMobile = (targetStatus, orders) =>
  shouldOpenMailForm(targetStatus, orders);

// 選択の中に工程を戻す注文が含まれるか(確認ダイアログの警告表示に使う)
export const hasRollback = (targetStatus, orders) =>
  (orders || []).some((order) => isRollback(order?.status, targetStatus));
